/**
 * Firm-wide Post-Completion dashboard.
 *
 * One screen showing every pending SDLT / AP1 / MR01 / redemption /
 * leasehold-notice item across every active case in the firm, sorted by
 * statutory deadline. The point is preventing missed deadlines: a partner
 * can scan this in 10 seconds and see exactly which items are overdue or
 * due-soon firm-wide.
 *
 * Filters: kind, status, solicitor, overdue-only toggle. Each row jumps
 * straight to the relevant case's Post-Completion tab.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API from "@/lib/api";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Stamp,
  Buildings,
  Bank,
  HouseLine,
  EnvelopeOpen,
  Warning,
  Clock,
  ArrowRight,
  Funnel,
  ArrowsClockwise,
} from "@phosphor-icons/react";

const KIND_META = {
  sdlt: { label: "SDLT Return", icon: Stamp, color: "text-amber-700", bg: "bg-amber-50" },
  ap1: { label: "AP1 — HMLR", icon: Buildings, color: "text-indigo-700", bg: "bg-indigo-50" },
  mr01: { label: "MR01 — CH", icon: Bank, color: "text-violet-700", bg: "bg-violet-50" },
  redemption: { label: "Redemption", icon: HouseLine, color: "text-emerald-700", bg: "bg-emerald-50" },
  leasehold_notice: { label: "Leasehold Notice", icon: EnvelopeOpen, color: "text-rose-700", bg: "bg-rose-50" },
};

const STATUS_LABELS = {
  not_started: "Not started",
  in_progress: "In progress",
  submitted: "Submitted",
  requisition: "Requisition",
  completed: "Completed",
  rejected: "Rejected",
};

function StatusBadge({ status }) {
  const map = {
    not_started: "bg-slate-100 text-slate-700",
    in_progress: "bg-blue-100 text-blue-700",
    submitted: "bg-amber-100 text-amber-700",
    requisition: "bg-orange-100 text-orange-800",
    completed: "bg-emerald-100 text-emerald-700",
    rejected: "bg-rose-100 text-rose-700",
  };
  return (
    <Badge className={`${map[status] || map.not_started} border-0`}>
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}

function DaysPill({ daysUntil, overdue }) {
  if (daysUntil === null || daysUntil === undefined) {
    return <span className="text-[11px] text-slate-400 italic">No date</span>;
  }
  const cls = overdue
    ? "bg-rose-600 text-white"
    : daysUntil <= 3
    ? "bg-amber-500 text-white"
    : daysUntil <= 7
    ? "bg-amber-100 text-amber-800"
    : "bg-slate-100 text-slate-700";
  const txt = overdue
    ? `${Math.abs(daysUntil)}d overdue`
    : daysUntil === 0
    ? "Today"
    : `${daysUntil}d`;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>
      <Clock weight="bold" className="w-3 h-3" />
      {txt}
    </span>
  );
}

export default function PostCompletionDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], summary: {}, solicitors: [] });
  const [filters, setFilters] = useState({
    kind: "all",
    status: "all",
    solicitor_id: "all",
    overdue_only: false,
    include_completed: false,
    search: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.kind !== "all") params.kind = filters.kind;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.solicitor_id !== "all") params.solicitor_id = filters.solicitor_id;
      if (filters.overdue_only) params.overdue_only = true;
      if (filters.include_completed) params.include_completed = true;
      const { data } = await API.get("/post-completion/dashboard", { params });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  // Refetch when filters change. Search is client-side only.
  useEffect(() => {
    load();
     
  }, [filters.kind, filters.status, filters.solicitor_id, filters.overdue_only, filters.include_completed]);

  const visibleItems = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((i) => {
      return (
        (i.case_title || "").toLowerCase().includes(q) ||
        (i.case_reference || "").toLowerCase().includes(q) ||
        (i.solicitor_name || "").toLowerCase().includes(q) ||
        (i.submission_ref || "").toLowerCase().includes(q)
      );
    });
  }, [data.items, filters.search]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Post-Completion Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Firm-wide view of every pending SDLT, AP1, MR01, redemption and leasehold-notice workflow. Sorted by statutory deadline.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="pc-dash-refresh"
          >
            <ArrowsClockwise weight="bold" className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Summary stat row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total open" value={data.summary?.total || 0} tone="slate" testid="pc-dash-stat-total" />
          <StatCard
            label="Overdue"
            value={data.summary?.overdue || 0}
            tone={data.summary?.overdue > 0 ? "rose" : "slate"}
            testid="pc-dash-stat-overdue"
          />
          <StatCard
            label="Due this week"
            value={data.summary?.due_this_week || 0}
            tone="amber"
            testid="pc-dash-stat-week"
          />
          <StatCard
            label="Solicitors involved"
            value={(data.solicitors || []).length}
            tone="slate"
            testid="pc-dash-stat-solicitors"
          />
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="pc-dash-filters">
          <Funnel weight="bold" className="w-4 h-4 text-slate-400 mb-2.5" />
          <div className="space-y-1 min-w-[140px]">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Workflow</Label>
            <Select value={filters.kind} onValueChange={(v) => setFilters((f) => ({ ...f, kind: v }))}>
              <SelectTrigger className="h-9 text-sm" data-testid="pc-dash-filter-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All workflows</SelectItem>
                {Object.entries(KIND_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 min-w-[140px]">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="h-9 text-sm" data-testid="pc-dash-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any status</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, l]) => (
                  <SelectItem key={k} value={k}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 min-w-[180px]">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Solicitor</Label>
            <Select value={filters.solicitor_id} onValueChange={(v) => setFilters((f) => ({ ...f, solicitor_id: v }))}>
              <SelectTrigger className="h-9 text-sm" data-testid="pc-dash-filter-solicitor">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All solicitors</SelectItem>
                {(data.solicitors || []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name || s.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex-1 min-w-[200px]">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Search</Label>
            <Input
              className="h-9 text-sm"
              placeholder="Case title, reference, solicitor, submission ref…"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              data-testid="pc-dash-search"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-700 mb-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.overdue_only}
              onChange={(e) => setFilters((f) => ({ ...f, overdue_only: e.target.checked }))}
              data-testid="pc-dash-overdue-toggle"
            />
            Overdue only
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-700 mb-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.include_completed}
              onChange={(e) => setFilters((f) => ({ ...f, include_completed: e.target.checked }))}
              data-testid="pc-dash-completed-toggle"
            />
            Include completed
          </label>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="pc-dash-table">
          {loading ? (
            <div className="p-12 text-center text-sm text-slate-500">Loading workflows…</div>
          ) : visibleItems.length === 0 ? (
            <div className="p-12 text-center">
              <Stamp weight="duotone" className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600 font-medium">No workflows match these filters.</p>
              <p className="text-xs text-slate-400 mt-1">
                Try removing the &quot;Overdue only&quot; toggle or selecting &quot;All workflows&quot;.
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="text-left px-4 py-3 font-semibold">Workflow</th>
                  <th className="text-left px-4 py-3 font-semibold">Case</th>
                  <th className="text-left px-4 py-3 font-semibold">Solicitor</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Submission Ref</th>
                  <th className="text-left px-4 py-3 font-semibold">Deadline</th>
                  <th className="text-right px-4 py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((it) => {
                  const meta = KIND_META[it.kind] || { label: it.kind, icon: Stamp, color: "text-slate-600", bg: "bg-slate-50" };
                  const Icon = meta.icon;
                  return (
                    <tr
                      key={it.id}
                      className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${it.is_overdue ? "bg-rose-50/30" : ""}`}
                      data-testid={`pc-dash-row-${it.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded ${meta.bg} ${meta.color}`}>
                            <Icon weight="duotone" className="w-4 h-4" />
                          </div>
                          <span className="font-medium text-slate-900">{meta.label}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 truncate max-w-[280px]">
                          {it.case_title || "Untitled case"}
                        </div>
                        {it.case_reference && (
                          <div className="text-[11px] text-slate-500 font-mono">{it.case_reference}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{it.solicitor_name || "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={it.status} /></td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700">{it.submission_ref || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          {it.deadline_date && (
                            <span className="text-xs text-slate-700">
                              {new Date(it.deadline_date).toLocaleDateString("en-GB")}
                            </span>
                          )}
                          <DaysPill daysUntil={it.days_until} overdue={it.is_overdue} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/cases/${it.case_id}?tab=post-completion`)}
                          className="text-[#1462a0] hover:bg-[#1462a0]/10 hover:text-[#1462a0]"
                          data-testid={`pc-dash-open-${it.id}`}
                        >
                          Open <ArrowRight weight="bold" className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {visibleItems.length > 0 && (
          <p className="text-xs text-slate-400 mt-3 text-center">
            Showing {visibleItems.length} of {data.items.length} workflow{data.items.length === 1 ? "" : "s"}.
            {(data.summary?.overdue || 0) > 0 && (
              <span className="text-rose-600 font-semibold ml-2">
                <Warning weight="fill" className="inline w-3 h-3" /> {data.summary.overdue} overdue
              </span>
            )}
          </p>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const toneCls = {
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    slate: "bg-white border-slate-200 text-slate-900",
  }[tone] || "bg-white border-slate-200 text-slate-900";
  return (
    <div className={`border rounded-lg p-4 ${toneCls}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-3xl font-bold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
    </div>
  );
}
